import { Button, DatePicker, Form, Input, InputNumber, Rate, Select, Typography, Divider } from "antd";
import dayjs from "dayjs";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { loadAllStaff } from "../../redux/rtk/features/user/userSlice";

const { TextArea } = Input;
const { Title } = Typography;
const { Option } = Select;

const AddEmployeeSkill = ({ form, onSubmit }) => {
	const dispatch = useDispatch();
	const [loader, setLoader] = React.useState(false);
	const staffList = useSelector((state) => state.users?.list || []);

	useEffect(() => {
		dispatch(loadAllStaff({ status: true }));
	}, [dispatch]);

	const onFinish = async (values) => {
		try {
			setLoader(true);
			const skillData = {
				...values,
				assessmentDate: dayjs(values.assessmentDate).format("YYYY-MM-DD"),
				acquiredDate: values.acquiredDate ? dayjs(values.acquiredDate).format("YYYY-MM-DD") : null,
				certificationDate: values.certificationDate ? dayjs(values.certificationDate).format("YYYY-MM-DD") : null,
				proficiencyLevel: values.proficiencyLevel || "Beginner",
				currentRating: values.currentRating || 0,
			};

			if (onSubmit) {
				onSubmit(skillData);
				toast.success("Employee Skill Recorded Successfully!");
			}
		} catch (error) {
			toast.error("Failed to record employee skill");
		} finally {
			setLoader(false);
		}
	};

	const onFinishFailed = (errorInfo) => {
		toast.warning("Please fill all required fields");
		setLoader(false);
	};

	return (
		<div className="column-design border rounded card-custom">
			<Title level={4} className="m-2 mt-5 mb-5 text-center">
				Employee Skills & Growth
			</Title>
			<Form
				form={form}
				style={{ marginBottom: "40px" }}
				name="employee-skill-form"
				labelCol={{ span: 8 }}
				wrapperCol={{ span: 16 }}
				onFinish={onFinish}
				onFinishFailed={onFinishFailed}
				autoComplete="off"
				preserve={false}
			>
				<Form.Item
					label={<span className="required-label">Employee</span>}
					name="employeeId"
					rules={[
						{ required: true, message: "Please select an employee!" },
					]}
				>
					<Select
						placeholder="Select employee"
						showSearch
						filterOption={(input, option) =>
							(option?.children ?? "")
								.toLowerCase()
								.includes(input.toLowerCase())
						}
					>
						{staffList?.map((staff) => (
							<Option key={staff.id} value={staff.id}>
								{staff.EmployeeName ||
									`${staff.firstName || ""} ${staff.lastName || ""}`.trim() ||
									staff.UserName}
							</Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item
					label={<span className="required-label">Skill Category</span>}
					name="skillCategory"
					rules={[
						{ required: true, message: "Please select skill category!" },
					]}
				>
					<Select placeholder="Select skill category">
						<Option value="Technical">Technical</Option>
						<Option value="Programming">Programming</Option>
						<Option value="Soft Skills">Soft Skills</Option>
						<Option value="Leadership">Leadership</Option>
						<Option value="Communication">Communication</Option>
						<Option value="Project Management">Project Management</Option>
						<Option value="Domain Knowledge">Domain Knowledge</Option>
						<Option value="Tools & Technologies">Tools & Technologies</Option>
						<Option value="Certification">Certification</Option>
					</Select>
				</Form.Item>

				<Form.Item
					label={<span className="required-label">Skill Name</span>}
					name="skillName"
					rules={[
						{ required: true, message: "Please enter skill name!" },
					]}
				>
					<Input placeholder="e.g., React.js, Python, Public Speaking, Agile Methodology" />
				</Form.Item>

				<Form.Item
					label={<span className="required-label">Proficiency Level</span>}
					name="proficiencyLevel"
					rules={[
						{ required: true, message: "Please select proficiency level!" },
					]}
				>
					<Select placeholder="Select proficiency level">
						<Option value="Beginner">Beginner</Option>
						<Option value="Intermediate">Intermediate</Option>
						<Option value="Advanced">Advanced</Option>
						<Option value="Expert">Expert</Option>
					</Select>
				</Form.Item>

				<Form.Item
					label={<span className="required-label">Current Rating</span>}
					name="currentRating"
					rules={[
						{ required: true, message: "Please provide current rating!" },
					]}
				>
					<Rate allowHalf />
				</Form.Item>

				<Form.Item
					label="Target Rating"
					name="targetRating"
				>
					<Rate allowHalf />
				</Form.Item>

				<Form.Item
					label={<span className="required-label">Assessment Date</span>}
					name="assessmentDate"
					rules={[
						{ required: true, message: "Please select assessment date!" },
					]}
				>
					<DatePicker
						style={{ width: "100%" }}
						format="DD-MM-YYYY"
					/>
				</Form.Item>

				<Form.Item
					label="Date Acquired"
					name="acquiredDate"
				>
					<DatePicker
						style={{ width: "100%" }}
						format="DD-MM-YYYY"
					/>
				</Form.Item>

				<Divider orientation="left">Certification & Training</Divider>

				<Form.Item
					label="Certification Name"
					name="certificationName"
				>
					<Input placeholder="e.g., AWS Certified Solutions Architect, PMP Certification" />
				</Form.Item>

				<Form.Item
					label="Certification Date"
					name="certificationDate"
				>
					<DatePicker
						style={{ width: "100%" }}
						format="DD-MM-YYYY"
					/>
				</Form.Item>

				<Form.Item
					label="Certification Provider"
					name="certificationProvider"
				>
					<Input placeholder="e.g., AWS, Microsoft, Google, Coursera" />
				</Form.Item>

				<Form.Item
					label="Training/Course Completed"
					name="trainingCompleted"
				>
					<TextArea
						rows={2}
						placeholder="List any training or courses completed for this skill"
						maxLength={300}
						showCount
					/>
				</Form.Item>

				<Divider orientation="left">Additional Information</Divider>

				<Form.Item
					label="Experience (Years)"
					name="experienceYears"
				>
					<InputNumber
						min={0}
						max={50}
						step={0.5}
						style={{ width: "100%" }}
						placeholder="Years of experience"
					/>
				</Form.Item>

				<Form.Item
					label="Projects/Work Examples"
					name="workExamples"
				>
					<TextArea
						rows={2}
						placeholder="Describe projects or work examples demonstrating this skill"
						maxLength={300}
						showCount
					/>
				</Form.Item>

				<Form.Item
					label="Notes"
					name="notes"
				>
					<TextArea
						rows={2}
						placeholder="Additional notes or comments"
						maxLength={300}
						showCount
					/>
				</Form.Item>

				<Form.Item
					label="Status"
					name="status"
					initialValue="Active"
				>
					<Select>
						<Option value="Active">Active</Option>
						<Option value="In Progress">In Progress</Option>
						<Option value="Completed">Completed</Option>
						<Option value="Expired">Expired</Option>
					</Select>
				</Form.Item>

				<Form.Item wrapperCol={{ offset: 8, span: 16 }}>
					<Button
						type="primary"
						size="large"
						htmlType="submit"
						block
						loading={loader}
					>
						Record Skill
					</Button>
				</Form.Item>
			</Form>
		</div>
	);
};

export default AddEmployeeSkill;

